// ═══════════════════════════════════════════════════════════════
// ContentImportDialog.tsx – fájl betöltése a pont tartalmaként (előnézettel)
//
// A fájlt a böngészőben dolgozza fel (nem kerül fel a szerverre, amíg nem kéred).
// A címsornak felismert első sor(oka)t alapból elhagyja (a pont címe már ott van).
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, RefreshCw, AlertTriangle, X, Check } from 'lucide-react';
import { extractDocument, type ImportedDocument } from '../../utils/documentImport';
import { RichTextView } from './RichTextView';

export interface ContentImportResult {
  html: string;
  /** csatolmány kezelése: új fájlnál „is maradjon”, meglévőnél „törlődjön” */
  attachmentChoice: boolean;
}

interface Props {
  /** a feldolgozandó fájl, vagy egy függvény, ami letölti (meglévő melléklet) */
  source: { file: Blob; filename: string } | { load: () => Promise<Blob>; filename: string };
  title?: string;
  pont?: string;
  /** 'editor': a szerkesztőbe tölt (még nem ment); 'record': azonnal felülírja a pont leírását */
  target: 'editor' | 'record';
  /** 'new': most kiválasztott fájl; 'existing': már feltöltött melléklet */
  origin: 'new' | 'existing';
  hasExistingContent?: boolean;
  onApply: (r: ContentImportResult) => Promise<void> | void;
  onClose: () => void;
}

export const ContentImportDialog: React.FC<Props> = ({ source, title, pont, target, origin, hasExistingContent, onApply, onClose }) => {
  const [doc, setDoc] = useState<ImportedDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dropHeading, setDropHeading] = useState(true);
  const [attachmentChoice, setAttachmentChoice] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const blob = 'file' in source ? source.file : await source.load();
        const d = await extractDocument(blob, source.filename, { title, pont });
        if (alive) setDoc(d);
      } catch (e: any) {
        if (alive) setError(e?.message || 'A fájl nem dolgozható fel');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented && !applying) { e.preventDefault(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, applying]);

  const html = doc ? ((dropHeading || !doc.headingHtml) ? doc.bodyHtml : doc.headingHtml + doc.bodyHtml) : '';
  const empty = doc && !html.replace(/<[^>]+>/g, '').trim() && !/<img|<table/i.test(html);

  const apply = async () => {
    setApplying(true);
    setError(null);
    try { await onApply({ html, attachmentChoice }); }
    catch (e: any) { setError(e?.message || 'Nem sikerült alkalmazni'); setApplying(false); }
  };

  return createPortal(
    <div data-esc-self className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
      onClick={(e) => { if (e.target === e.currentTarget && !applying) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="cimp-title"
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
        <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <div className="min-w-0">
              <h2 id="cimp-title" className="text-sm font-bold text-slate-900 dark:text-white">Betöltés tartalomként</h2>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate" title={source.filename}>
                {source.filename}{doc?.pages ? ` · ${doc.pages} oldal` : ''}{pont ? ` → ${pont} pont` : ''}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={applying} className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg cursor-pointer" aria-label="Bezárás"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3 text-xs">
          {!doc && !error && (
            <div className="py-12 flex flex-col items-center gap-2 text-slate-400"><RefreshCw className="w-5 h-5 animate-spin" /> A fájl szövegének kinyerése…</div>
          )}
          {error && (
            <div className="p-3 rounded-xl bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 flex items-start gap-2" role="alert">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </div>
          )}
          {doc && (
            <>
              {doc.warnings.map((w, i) => (
                <div key={i} className="p-2.5 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {w}
                </div>
              ))}
              {doc.headingText && (
                <label className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={dropHeading} onChange={(e) => setDropHeading(e.target.checked)} />
                  <span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">Első sor (címsor) elhagyása</span>
                    <span className="block text-slate-500 dark:text-slate-400 line-clamp-2">„{doc.headingText}”</span>
                  </span>
                </label>
              )}
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Előnézet</div>
                <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 max-h-[40vh] overflow-y-auto">
                  {empty ? <span className="text-slate-400">(nincs tartalom)</span> : <RichTextView html={html} className="text-sm text-slate-800 dark:text-slate-200" />}
                </div>
              </div>
              {target === 'record' && hasExistingContent && (
                <p className="text-amber-800 dark:text-amber-300 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> A pont jelenlegi leírása ezzel felülíródik.</p>
              )}
              {target === 'record' && (
                <label className="flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={attachmentChoice} onChange={(e) => setAttachmentChoice(e.target.checked)} />
                  {origin === 'new' ? 'A fájl csatolmányként is kerüljön fel' : 'A csatolmány törlése a betöltés után'}
                </label>
              )}
              {target === 'editor' && <p className="text-slate-500 dark:text-slate-400">A tartalom a szerkesztőbe kerül (a jelenlegi helyére) – mentés előtt még módosíthatod.</p>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={applying} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer">Mégse</button>
          <button type="button" onClick={apply} disabled={!doc || !!empty || applying}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
            {applying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {target === 'record' ? 'Betöltés a pont tartalmaként' : 'Betöltés a szerkesztőbe'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContentImportDialog;
